'use client';

import { c as _c } from "react/compiler-runtime";
import './modal.scss';
import type { DialogProps as RdxRootProps } from '@radix-ui/react-dialog';
import { Close as RdxClose, Content as RdxContent, Overlay as RdxOverlay, Portal as RdxPortal, Root as RdxRoot, Trigger as RdxTrigger } from '@radix-ui/react-dialog';
import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef } from 'react';
import { type JSX, createContext, use, useEffect, useRef } from 'react';
import { CloseButton } from '../../atoms/close-button/close-button.tsx';
import { cn } from '../../utils/helpers.ts';

/**
 * Shared modal state provided by the Root to every part (content, close button).
 * `dismissible` governs USER-initiated close (Esc, outside click, X). It does
 * NOT affect programmatic close via `open`/`onOpenChange`.
 */
interface ModalContextValue {
  controlledTriggerRef: React.RefObject<HTMLElement | null>;
  dismissible: boolean;
  isBusy: boolean;
}
const ModalContext = createContext<ModalContextValue | undefined>(undefined);

/**
 * Controlled triggers are used outside the Modal context, so this is the smoothest
 * way for open Modals to know which trigger opened it. Whenever a trigger is clicked,
 * it adds this attribute, the modal finds it and stores the ref, then removes it from the trigger.
 * The one catch here is an extremely unlikely scenario when a dev accidentally adds a trigger that doesn't activate a modal.
 * If that is clicked, and later the very Modal is opened programmatically without any trigger, it will pick up the previous
 * trigger and restore it focus to it when closed. We have a lint warning devs to ensure a controlled trigger is always controlling
 * the open state of a Modal (which is the very purpose of the controlled trigger).
 */
const controlledTriggerAttribute = 'data-modal-controlled-trigger';
export const useModalContext = () => {
  const context = use(ModalContext);
  if (!context) {
    throw new Error('Modal components must be rendered within Modal');
  }
  return context;
};
export interface ModalProps extends ComponentPropsWithoutRef<'div'> {
  defaultOpen?: RdxRootProps['defaultOpen'];
  dismissible?: boolean;
  isBusy?: boolean;
  modal?: RdxRootProps['modal'];
  onClose?: () => void;
  onOpen?: () => void;
  onOpenChange?: RdxRootProps['onOpenChange'];
  open?: RdxRootProps['open'];
}
export const Modal: React.FC<ModalProps> = t0 => {
  const $ = _c(27);
  let children;
  let onClose;
  let onOpen;
  let onOpenChange;
  let open;
  let rdxProps;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      dismissible: t1,
      isBusy: t2,
      onClose,
      onOpen,
      onOpenChange,
      open,
      ...rdxProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClose;
    $[3] = onOpen;
    $[4] = onOpenChange;
    $[5] = open;
    $[6] = rdxProps;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    onClose = $[2];
    onOpen = $[3];
    onOpenChange = $[4];
    open = $[5];
    rdxProps = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const dismissible = t1 === undefined ? true : t1;
  const isBusy = t2 === undefined ? false : t2;
  const controlledTriggerRef = useRef(null);
  let t3;
  let t4;
  if ($[9] !== open) {
    t3 = () => {
      if (!open) {
        return;
      }
      const controlledTrigger = globalThis.document.querySelector(`[${controlledTriggerAttribute}]`);
      if (!controlledTrigger) {
        return;
      }
      controlledTriggerRef.current = controlledTrigger;
      controlledTrigger.removeAttribute(controlledTriggerAttribute);
    };
    t4 = [open];
    $[9] = open;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== dismissible || $[13] !== isBusy) {
    t5 = {
      controlledTriggerRef,
      dismissible,
      isBusy
    };
    $[12] = dismissible;
    $[13] = isBusy;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== onClose || $[16] !== onOpen || $[17] !== onOpenChange) {
    t6 = nextOpen => {
      if (onOpenChange) {
        onOpenChange(nextOpen);
      }
      if (onOpen && nextOpen) {
        onOpen();
      } else {
        if (onClose && !nextOpen) {
          onClose();
        }
      }
    };
    $[15] = onClose;
    $[16] = onOpen;
    $[17] = onOpenChange;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== children || $[20] !== open || $[21] !== rdxProps || $[22] !== t6) {
    t7 = <RdxRoot open={open} onOpenChange={t6} {...rdxProps}>{children}</RdxRoot>;
    $[19] = children;
    $[20] = open;
    $[21] = rdxProps;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t5 || $[25] !== t7) {
    t8 = <ModalContext value={t5}>{t7}</ModalContext>;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};

/**
 * Modal content.
 */
export interface ModalContentProps extends ComponentPropsWithoutRef<typeof RdxContent> {
  className?: string;
}
export const ModalContent: React.FC<ModalContentProps & {
  ref?: React.Ref<HTMLDivElement>;
}> = ({
  style,
  className,
  ref,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props
}) => {
  const {
    controlledTriggerRef,
    dismissible
  } = useModalContext();
  const handleCloseAutoFocus: ModalContentProps['onCloseAutoFocus'] = event => {
    onCloseAutoFocus?.(event);
    const controlledTrigger = controlledTriggerRef.current;
    controlledTriggerRef.current = null;
    if (event.defaultPrevented) {
      return;
    }
    if (!controlledTrigger?.isConnected) {
      return;
    }
    controlledTrigger.focus();
    if (controlledTrigger.ownerDocument.activeElement === controlledTrigger) {
      event.preventDefault();
    }
  };
  const handleEscapeKeyDown: ModalContentProps['onEscapeKeyDown'] = event_0 => {
    if (!dismissible) {
      event_0.preventDefault();
    }
    onEscapeKeyDown?.(event_0);
  };
  const handlePointerDownOutside: ModalContentProps['onPointerDownOutside'] = event_1 => {
    if (!dismissible) {
      event_1.preventDefault();
    }
    onPointerDownOutside?.(event_1);
  };
  return <RdxPortal>
      <RdxOverlay className="Modal__overlay" />
      <RdxContent ref={ref} aria-describedby={undefined} className={cn('Modal__content', {
      [`${className}`]: !!className
    })} style={{
      ...style
    }} onCloseAutoFocus={handleCloseAutoFocus} onEscapeKeyDown={handleEscapeKeyDown} onPointerDownOutside={handlePointerDownOutside} {...props} tabIndex={undefined}>
        {props.children}
      </RdxContent>
    </RdxPortal>;
};

/**
 * Modal trigger.
 */
export type ModalTriggerProps = {
  children: JSX.Element;
};
export const ModalTrigger: React.FC<ModalTriggerProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <RdxTrigger asChild={true}>{children}</RdxTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export type ModalControlledTriggerProps = {
  children: JSX.Element;
};
export const ModalControlledTrigger: React.FC<ModalControlledTriggerProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Slot onClick={_temp}>{children}</Slot>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * Modal close.
 */
export const ModalClose = RdxClose;

/**
 * Modal title.
 */

/**
 * Modal close button.
 */
export const ModalCloseButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  name?: string;
}> = ({
  className,
  name,
  disabled,
  ...props
}) => {
  const {
    dismissible
  } = useModalContext();
  return <ModalClose asChild>
      <CloseButton className={cn('Modal__close', {
      [`${className}`]: !!className
    })} aria-label={`Close ${name}`} disabled={disabled || !dismissible} {...props} />
    </ModalClose>;
};
export { Title as ModalTitle } from '@radix-ui/react-dialog';
function _temp(event) {
  const {
    ownerDocument
  } = event.currentTarget;
  for (const controlledTrigger of ownerDocument.querySelectorAll(`[${controlledTriggerAttribute}]`)) {
    controlledTrigger.removeAttribute(controlledTriggerAttribute);
  }
  event.currentTarget.setAttribute(controlledTriggerAttribute, "");
}